// Feature 12: Insight Explainability Service

import { api } from './api';
import {
  InsightExplanation,
  ProvenanceDAG,
  EvidenceItem,
  InsightExplanationRequest,
} from '../types/insightExplainability.types';

/**
 * Get complete explanation for an insight
 */
export const getInsightExplanation = async (
  insightId: string,
  options?: Partial<InsightExplanationRequest>
): Promise<InsightExplanation> => {
  const params = {
    includeProvenance: options?.includeProvenance ?? true,
    includeEvidence: options?.includeEvidence ?? true,
    includeAlternatives: options?.includeAlternatives ?? false,
  };

  const response = await api.get(`/earlystage/insights/${insightId}/explanation`, { params });
  return response.data;
};

/**
 * Get provenance DAG for an insight
 */
export const getInsightProvenance = async (insightId: string): Promise<ProvenanceDAG> => {
  const response = await api.get(`/earlystage/insights/${insightId}/provenance`);
  return response.data;
};

/**
 * Get evidence chain for an insight
 */
export const getInsightEvidence = async (insightId: string): Promise<EvidenceItem[]> => {
  const response = await api.get(`/earlystage/insights/${insightId}/evidence`);
  return response.data;
};

/**
 * Recompute insight explanation with latest data
 */
export const recomputeInsightExplanation = async (insightId: string): Promise<InsightExplanation> => {
  const response = await api.post(`/earlystage/insights/${insightId}/explanation/recompute`);
  return response.data;
};

/**
 * Get alternative explanations for an insight
 */
export const getAlternativeExplanations = async (
  insightId: string
): Promise<{ explanations: string[]; confidence: number[] }> => {
  const response = await api.get(`/earlystage/insights/${insightId}/alternatives`);
  return response.data;
};

/**
 * Validate causality claim for an insight
 */
export const validateCausality = async (
  insightId: string,
  validationMethod?: 'GRANGER' | 'INSTRUMENTAL_VARIABLE' | 'PROPENSITY_SCORE'
): Promise<{
  causalitySupported: boolean;
  testStatistic: number;
  pValue: number;
  method: string;
}> => {
  const response = await api.post(`/insights/${insightId}/validate-causality`, {
    method: validationMethod ?? 'GRANGER',
  });
  return response.data;
};
